import { crm } from "../../../theme";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FaCalendarAlt, FaPen, FaPlus, FaTrash } from "react-icons/fa";
import {
  ExchangeCondition,
  describeCondition,
} from "../../../models/ExchangeCondition";
import {
  deleteExchangeCondition,
  getExchangeConditions,
} from "../../../services/exchangeConditionService";
import CreateExchangeConditionModal from "./CreateExchangeConditionModal";

const ExchangeConditions = () => {
  const [conditions, setConditions] = useState<ExchangeCondition[]>([]);
  const [editing, setEditing] = useState<ExchangeCondition | undefined>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const load = async () => {
    const response = await getExchangeConditions();
    setConditions(response ?? []);
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id?: string) => {
    if (!id) return;
    await deleteExchangeCondition(id);
    await load();
    toast({
      title: "Condição removida.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box>
      <Heading size="lg" mb="24px" fontWeight="normal">
        <Flex alignItems="center" gap="12px">
          <FaCalendarAlt color="var(--icon-color)" /> Condições de câmbio
        </Flex>
      </Heading>

      <Text mb="24px" color={crm.textMuted}>
        Cadastre condições reutilizáveis (data de referência + dias) para
        preencher automaticamente a Data do Câmbio da 2ª parcela / parcela única
        nos processos.
      </Text>

      <Button
        leftIcon={<FaPlus />}
        backgroundColor={crm.brand}
        color="white"
        borderRadius="10px"
        _hover={{ backgroundColor: crm.brandDark }}
        mb="24px"
        onClick={() => {
          setEditing(undefined);
          onOpen();
        }}
      >
        Nova condição
      </Button>

      <Flex
        flexDir="column"
        gap="12px"
        backgroundColor="white"
        borderRadius="12px"
        border={`1px solid ${crm.border}`}
        boxShadow={crm.shadowSm}
        p="24px"
      >
        {conditions.length === 0 && (
          <Text color={crm.textFaint}>Nenhuma condição cadastrada ainda.</Text>
        )}
        {conditions.map((c) => (
          <Flex
            key={c.id}
            alignItems="center"
            justifyContent="space-between"
            borderWidth="1px"
            borderRadius="md"
            p="12px 16px"
          >
            <Box>
              <Heading size="sm" fontWeight="semibold">
                {c.name}
              </Heading>
              <Text color={crm.textMuted} fontSize="sm">
                {describeCondition(c)}
              </Text>
            </Box>
            <Flex gap="8px">
              <IconButton
                aria-label="Editar"
                icon={<FaPen />}
                size="sm"
                variant="ghost"
                color={crm.brand}
                borderRadius="10px"
                _hover={{ bg: crm.brandSoft }}
                onClick={() => {
                  setEditing(c);
                  onOpen();
                }}
              />
              <IconButton
                aria-label="Remover"
                icon={<FaTrash />}
                size="sm"
                colorScheme="red"
                onClick={() => remove(c.id)}
              />
            </Flex>
          </Flex>
        ))}
      </Flex>

      <CreateExchangeConditionModal
        isOpen={isOpen}
        onClose={onClose}
        condition={editing}
        onSaved={() => load()}
      />
    </Box>
  );
};

export default ExchangeConditions;
